import React, { useCallback, useEffect, useState } from "react";
import {
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Box,
  TextField,
  Checkbox,
  Button,
} from "@mui/material";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { Link, NavLink } from "react-router-dom";
import { RemoveRedEye as RemoveRedEyeIcon } from "@mui/icons-material";
import agent from "../../utils/axios";
import LoadingSpinner from "../Loading/LoadingSpinner";
import { Authenticate } from "../../types/Authenticate";

interface AuthUrlResponse {
  url: string;
}

const Paper = styled(MuiPaper)(spacing);

type HeadCell = {
  id: string;
  alignment: "left" | "center" | "right" | "justify" | "inherit" | undefined;
  label: string;
  disablePadding?: boolean;
};

const headCells: Array<HeadCell> = [
  { id: "payeeId", alignment: "left", label: "Payee ID" },
  { id: "payeeName", alignment: "left", label: "Payee Name" },
  { id: "invoiceId", alignment: "left", label: "Invoice Id" },
  { id: "invoiceDate", alignment: "left", label: "Invoice Date" },
  { id: "billRefCode", alignment: "left", label: "Bill Ref Code" },
  { id: "billStatus", alignment: "left", label: "Bill Status" },
  { id: "billAmount", alignment: "left", label: "Amount" },
  { id: "paymentMethod", alignment: "left", label: "Payment Method" },
  { id: "errorMessage", alignment: "left", label: "Error" },
  { id: "", alignment: "right", label: "Actions" },
];

type RowType = {
  [key: string]: any;
  payeeId: string;
  payeeName: string;
  invoiceDate: string;
  invoiceId: string;
  billRefCode: string;
  paymentRefCode: string;
  billStatus: string;
  billAmount: number;
  paymentStatus: string;
  paymentMethod: string;
  errorMessage?: string;
};

const PendingBills = () => {
  const [loading, setLoading] = React.useState(true);
  const [order, setOrder] = useState<"desc" | "asc">("asc");
  const [orderBy, setOrderBy] = useState("billStatus");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [data, setData] = useState<Array<RowType>>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  // Selection handlers
  const isSelected = (billRefCode: string) =>
    selected.indexOf(billRefCode) !== -1;

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = data.map((n) => n.billRefCode);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (
    event: React.MouseEvent<unknown>,
    billRefCode: string
  ) => {
    const selectedIndex = selected.indexOf(billRefCode);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, billRefCode);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const getPayees = useCallback(async () => {
    setLoading(true);
    try {
      const response = await agent.Bills.getAllFailedBills(
        page,
        rowsPerPage,
        orderBy,
        order,
        searchTerm
      );
      setData(response.data); // Adjust based on actual response structure
      setTotalCount(response.count); // Assume totalCount comes from the response
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, orderBy, order, searchTerm]);

  useEffect(() => {
    getPayees();
  }, [page, rowsPerPage, searchTerm, getPayees]);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0); // Reset to the first page on sort change
    getPayees(); // Fetch data with new sorting
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    getPayees(); // Fetch new page data
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    getPayees(); // Fetch data for updated rows per page
  };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };
  const handleConvertToCheck = async () => {
    setLoading(true);
    try {
      const { url }: AuthUrlResponse = await agent.quickbooks.getAuthUrl();
      if (url) {
        const urlParams = new URLSearchParams(new URL(url).search);
        const redirect_uri = urlParams.get("redirect_uri") ?? "";
        const popupWidth = 600;
        const popupHeight = 700;
        const left = window.screen.width / 2 - popupWidth / 2;
        const top = window.screen.height / 2 - popupHeight / 2;

        const authWindow = window.open(
          url,
          "QuickBooks Login",
          `width=${popupWidth},height=${popupHeight},left=${left},top=${top}`
        );

        // Check if the popup opened successfully
        if (!authWindow) {
          setLoading(false);
          return;
        }

        const authCheckInterval = setInterval(() => {
          try {
            if (authWindow.location.href.includes("code=")) {
              const params = new URLSearchParams(authWindow.location.search);
              const code = params.get("code");
              if (code) {
                clearInterval(authCheckInterval);
                authWindow.close();
                handleAuthCode(code, redirect_uri);
              }
            }
          } catch (error) {
            // Ignore cross-origin errors until the popup is redirected to your domain with the code.
          }

          if (authWindow.closed) {
            clearInterval(authCheckInterval);
          }
        }, 200);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const handleAuthCode = async (code: string | null, redirect_uri: string) => {
    if (code) {
      try {
        const authRequest: Authenticate = { code };

        await agent.quickbooks.authenticateQuickbooks(
          authRequest,
          redirect_uri
        );
        await agent.Bills.convertToCheck(selected ?? "");
        window.location.reload();
      } catch (error) {
        console.error("Error processing QuickBooks authentication:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) return <LoadingSpinner />;
  return (
    <>
      <Box display="flex" gap={2} mb={4} mt={2}>
        <TextField
          label="Search Bills"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by Payee ID, Name, Bill Ref Code..."
          sx={{ minWidth: 550 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleConvertToCheck}
          disabled={selected.length === 0}
        >
          Convert to Check
        </Button>
      </Box>
      {loading && <LoadingSpinner />}
      <Paper>
        <TableContainer>
          <Table
            aria-labelledby="tableTitle"
            size={"medium"}
            aria-label="enhanced table"
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selected.length > 0 && selected.length < data.length
                    }
                    checked={data.length > 0 && selected.length === data.length}
                    onChange={handleSelectAllClick}
                    inputProps={{
                      "aria-label": "select all bills",
                    }}
                  />
                </TableCell>
                {headCells.map((headCell, index) => (
                  <TableCell
                    key={index}
                    align={headCell.alignment}
                    padding={headCell.disablePadding ? "none" : "normal"}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : "asc"}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((row, index) => {
                const isItemSelected = isSelected(row.billRefCode);
                return (
                  <TableRow
                    key={index}
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        onClick={(event) => handleClick(event, row.billRefCode)}
                        inputProps={{
                          "aria-labelledby": `enhanced-table-checkbox-${index}`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/payees/detail/${row.payeeId}`}
                        style={{ color: "black" }}
                      >
                        {row.payeeId}
                      </Link>
                    </TableCell>
                    <TableCell>{row.payeeName}</TableCell>
                    <TableCell>{row.invoiceId}</TableCell>
                    <TableCell>
                      {new Intl.DateTimeFormat("en-US", {
                        dateStyle: "medium",
                      }).format(new Date(row.invoiceDate))}
                    </TableCell>
                    <TableCell>{row.billRefCode}</TableCell>
                    <TableCell>{row.billStatus}</TableCell>
                    <TableCell>
                      {new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(row.billAmount)}
                    </TableCell>
                    <TableCell>{row.paymentMethod}</TableCell>
                    <TableCell
                      sx={{
                        color: row.errorMessage ? "error.main" : "inherit",
                        maxWidth: 300,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={row.errorMessage || ""}
                    >
                      {row.errorMessage || "-"}
                    </TableCell>
                    <TableCell padding="none" align="right">
                      <Box mr={2}>
                        <IconButton
                          aria-label="details"
                          component={NavLink}
                          to={`/bills/detail/${row.billRefCode}`}
                          size="large"
                          disabled={!row?.billRefCode}
                        >
                          <RemoveRedEyeIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 250]}
          component="div"
          count={totalCount} // Use totalCount here
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );
};

export default PendingBills;
